import { Link } from 'react-router-dom';

import type { Match, MatchRuleSet, Team, TeamId } from '@/core/schemas';
import { cn } from '@/shared/utils/cn';

import {
  MATCH_STATUS_LABELS,
  MATCH_STATUS_TONES,
  describeRules,
  scoreLabel,
  sideLabel,
} from './matchPresentation';

interface MatchRowProps {
  match: Match;
  lookup: (teamId: TeamId) => Team | undefined;
  rules: MatchRuleSet | null;
  /** Highlights the captain's own team. */
  highlightTeamId?: TeamId | undefined;
  /** When set, the card links to the match detail page. */
  to?: string | undefined;
}

/**
 * One match as a self-contained card. Used by the schedule, the dashboard and
 * the beamer view alike, so a status looks the same everywhere it appears.
 */
export function MatchRow({ match, lookup, rules, highlightTeamId, to }: MatchRowProps) {
  const isOwn = (side: Match['home']) =>
    highlightTeamId !== undefined && side.kind === 'team' && side.teamId === highlightTeamId;

  const winner = match.result?.winnerTeamId ?? null;
  const isWinner = (side: Match['home']) => side.kind === 'team' && side.teamId === winner;

  const content = (
    <>
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <span className="min-w-0 flex-1 truncate text-sm">
          <span
            className={cn(
              isOwn(match.home) && 'text-brand font-semibold',
              isWinner(match.home) && 'font-semibold',
            )}
          >
            {sideLabel(match.home, lookup)}
          </span>
        </span>

        <span className="text-fg-subtle shrink-0 font-mono text-xs">
          {scoreLabel(match) === '' ? 'vs' : scoreLabel(match)}
        </span>

        <span className="min-w-0 flex-1 truncate text-right text-sm sm:text-left">
          <span
            className={cn(
              isOwn(match.away) && 'text-brand font-semibold',
              isWinner(match.away) && 'font-semibold',
            )}
          >
            {sideLabel(match.away, lookup)}
          </span>
        </span>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {match.boardNumber !== null && (
          <span className="border-line text-fg-muted rounded border px-2 py-0.5 text-xs">
            Board {match.boardNumber}
          </span>
        )}
        {rules !== null && (
          <span className="text-fg-subtle hidden text-xs sm:inline">{describeRules(rules)}</span>
        )}
        <span
          className={cn(
            'rounded px-2 py-0.5 text-xs font-medium',
            MATCH_STATUS_TONES[match.status],
          )}
        >
          {MATCH_STATUS_LABELS[match.status]}
        </span>
      </div>
    </>
  );

  const cardClass =
    'border-line bg-surface flex flex-col gap-2 rounded-lg border p-3 sm:flex-row sm:items-center sm:gap-4';

  if (to !== undefined) {
    return (
      <li>
        <Link to={to} className={cn(cardClass, 'hover:border-line-strong transition-colors')}>
          {content}
        </Link>
      </li>
    );
  }

  return <li className={cardClass}>{content}</li>;
}
